import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, ClipboardList, Clock, Target, ArrowRight } from 'lucide-react';
import api from '../services/api';
import AssessmentTimer from '../components/AssessmentTimer';

function Assessment() {
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get('enrollmentId');
  const sectionOrder = searchParams.get('sectionOrder');
  const programId = searchParams.get('programId');
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [attemptNumber, setAttemptNumber] = useState(null);
  const [initialTimeRemaining, setInitialTimeRemaining] = useState(null);
  const [startedAt, setStartedAt] = useState(null);

  useEffect(() => {
    const fetchAndStartAssessment = async () => {
      try {
        // Fetch assessment using program ID and section order
        const assessmentRes = await api.get(`/assessments/program/${programId}/section/${sectionOrder}`);
        setAssessment(assessmentRes.data);

        // Start assessment (creates or retrieves in-progress submission)
        const startRes = await api.post(`/assessments/${assessmentRes.data._id}/start`, {
          enrollmentId
        });

        if (startRes.data.expired) {
          setError('Assessment time has already expired');
          // Auto-submit with empty answers
          await handleSubmit(null, true);
        } else {
          setSubmission(startRes.data.submission);
          setAttemptNumber(startRes.data.attemptNumber);
          setInitialTimeRemaining(startRes.data.timeRemaining);
          setStartedAt(startRes.data.startedAt);
        }
      } catch (error) {
        console.error('Error fetching assessment:', error);
        setError('Failed to load assessment');
      } finally {
        setLoading(false);
      }
    };

    if (programId && sectionOrder) {
      fetchAndStartAssessment();
    } else {
      setError('Missing program or section information');
      setLoading(false);
    }
  }, [programId, sectionOrder, enrollmentId]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleTimeExpired = async () => {
    await handleSubmit(null, true);
  };

  const handleSubmit = async (e, autoSubmit = false) => {
    if (e) e.preventDefault();
    
    setSubmitting(true);
    setError('');

    try {
      // Format answers for submission
      const formattedAnswers = Object.keys(answers).map(questionId => ({
        questionId,
        answer: answers[questionId]
      }));

      // Submit assessment with submission ID
      const submitRes = await api.post(`/assessments/${assessment._id}/submit`, {
        answers: formattedAnswers,
        enrollmentId,
        submissionId: submission._id
      });

      if (autoSubmit) {
        alert('Time expired! Your assessment has been automatically submitted.');
      }

      // Check if passed
      if (!submitRes.data.passed) {
        alert(`You scored ${submitRes.data.percentage}%. You need at least ${assessment.passingScore}% to pass. Please try again.`);
        navigate(`/learning/${enrollmentId}`);
        return;
      }

      // Update section progress only if passed
      await api.patch(`/enrollments/${enrollmentId}/section/${sectionOrder}`, {
        assessmentScore: submitRes.data.percentage
      });

      alert(`Congratulations! You passed with ${submitRes.data.percentage}%. Moving to next section.`);
      // Navigate back to learning path
      navigate(`/learning/${enrollmentId}`);
    } catch (err) {
      if (err.response?.data?.passed === false) {
        alert(err.response.data.message);
        navigate(`/learning/${enrollmentId}`);
      } else if (err.response?.data?.expired) {
        alert('Assessment time expired. Please start again.');
        navigate(`/learning/${enrollmentId}`);
      } else {
        setError(err.response?.data?.message || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700">{error || 'Assessment not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Timer Component */}
      {submission && assessment && initialTimeRemaining !== null && (
        <AssessmentTimer
          assessmentId={assessment._id}
          submissionId={submission._id}
          initialTimeRemaining={initialTimeRemaining}
          startedAt={startedAt}
          duration={assessment.duration}
          onTimeExpired={handleTimeExpired}
        />
      )}

      {/* Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
              <ClipboardList className="w-9 h-9 text-slate-900" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-center">
            {assessment.title}
          </h1>
          <p className="text-slate-300 text-center text-lg mb-6">
            {assessment.description}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
              <Clock className="w-5 h-5" />
              <span className="font-medium">{assessment.duration} minutes</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
              <Target className="w-5 h-5" />
              <span className="font-medium">Passing: {assessment.passingScore}%</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">{assessment.totalPoints} points</span>
            </div>
            {attemptNumber && (
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                <span className="font-medium">📝 Attempt #{attemptNumber}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Questions Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {assessment.questions?.map((question, idx) => (
            <div
              key={question._id}
              className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 hover:border-slate-300 transition-all"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-slate-900 text-white rounded-lg flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
                      {question.type === 'fill-blank' ? 'Fill in the Blank' : 'Multiple Choice'}
                    </span>
                    <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full font-semibold">
                      {question.points} points
                    </span>
                  </div>
                  <p className="text-lg text-slate-900 font-medium">{question.question}</p>
                </div>
              </div>

              <div className="ml-14">
                {question.type === 'fill-blank' ? (
                  <input
                    type="text"
                    placeholder="Type your answer here..."
                    onChange={(e) => handleAnswerChange(question._id, e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                    required
                  />
                ) : (
                  <div className="space-y-3">
                    {question.options?.map((option) => (
                      <label
                        key={option}
                        className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-all group"
                      >
                        <input
                          type="radio"
                          name={`question-${question._id}`}
                          value={option}
                          onChange={(e) => handleAnswerChange(question._id, e.target.value)}
                          className="w-4 h-4 text-slate-900 focus:ring-2 focus:ring-slate-900"
                          required
                        />
                        <span className="text-slate-700 group-hover:text-slate-900 font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="px-8 py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Assessment
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Assessment;
